import { CalendarIcon, ChartBarIcon } from '@heroicons/react/24/outline'
import ctl from '@netlify/classnames-template-literals'
import { motion } from 'motion/react'
import { JobiLoader } from '../../rapid-ui/jobiloader'
import { CampaignInvoiceViewModel } from './campaign-invoice.vm'
import { CampaignNav } from './wrapper'

export function CampaignInvoice({ view, response, loading }: CampaignInvoiceViewModel) {
	const isLoading = loading.invoice
	const invoice = view.invoice
	const isInvoiceNotFound = response.invoice?.status === 'invoice_not_found'

	return (
		<CampaignNav activeCampaignNavLink="invoice">
			<div className="grid grid-rows-[1fr] min-h-[inherit] gap-4 py-4">
				{isLoading
					? (
						<div className="text-sm text-grey-500 flex items-center justify-center h-full">
							<div className="flex items-center flex-col gap-6 max-w-[350px]">
								<JobiLoader />
							</div>
						</div>
					)
					: isInvoiceNotFound
					? (
						<div className="text-sm text-grey-500 flex items-center justify-center h-full">
							<div className="flex items-center flex-col gap-6 max-w-[350px]">
								<div className="scale-200 text-brand-400">
									<ChartBarIcon className="size-6" />
								</div>
								<div className="text-sm text-grey-500 text-center">
									No invoice found for this campaign.
								</div>
							</div>
						</div>
					)
					: !invoice
					? (
						<div className="text-sm text-grey-500 flex items-center justify-center h-full">
							<div className="flex items-center flex-col gap-6 max-w-[350px]">
								<div className="scale-200 text-brand-400">
									<ChartBarIcon className="size-6" />
								</div>
								<div className="text-sm text-grey-500 text-center">
									Unable to load invoice data. Please try again later.
								</div>
							</div>
						</div>
					)
					: (
						<div className="px-8 space-y-6">
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.2, delay: 0.3 }}
								className="relative overflow-hidden p-6 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 rounded-xl border border-slate-200/60 shadow-sm"
							>
								<div className="relative">
									<div className="flex items-center justify-between mb-4">
										<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500">
											Invoice Details
										</h2>
										{view.invoice && view.invoice.hostedInvoiceUrl && (
											<a
												href={view.invoice.hostedInvoiceUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="text-sm text-purple-600 hover:text-purple-800 font-medium underline"
											>
												View Invoice
											</a>
										)}
									</div>
									{view.invoice && (
										<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
											<InfoRow
												label="Status"
												value={view.invoice.status}
												className="capitalize"
											/>
											<InfoRow
												label="Amount Due"
												value={formatCurrency(
													(view.invoice.amountDue - view.invoice.amountPaid)
														/ 100,
												)}
											/>
											<InfoRow
												label="Due Date"
												value={view.invoice.dueDate
													? formatDate(view.invoice.dueDate)
													: 'N/A'}
												icon={<CalendarIcon className="size-5" />}
											/>
										</div>
									)}
								</div>
							</motion.div>
						</div>
					)}
			</div>
		</CampaignNav>
	)
}

function InfoRow({
	label,
	value,
	icon,
	className,
}: {
	label: string
	value: string
	icon?: React.ReactNode
	className?: string
}) {
	return (
		<div className="flex flex-col p-4 rounded-lg bg-white/40 border border-slate-200/40 hover:bg-white/60 hover:border-slate-300/60 transition-all duration-200">
			<div className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-3">{label}</div>
			<div className="flex items-center gap-2">
				{icon && <div className="text-grey-600 flex-shrink-0">{icon}</div>}
				<div className={ctl(`${className} text-base font-semibold text-grey-900 truncate`)}>{value}</div>
			</div>
		</div>
	)
}

function formatCurrency(amount: number): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount)
}

function formatDate(date: string | Date): string {
	const dateObj = typeof date === 'string' ? new Date(date) : date
	return new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}).format(dateObj)
}
